import { useState, useEffect, useRef } from 'react'
import { batchAPI } from '../../services/api'

const MODEL_OPTIONS = [
  { value: 'gpt-4.1-nano', label: 'GPT-4.1-nano (cheapest)', costPer1k: 27 },
  { value: 'gpt-4o-mini', label: 'GPT-4o-mini', costPer1k: 57 },
  { value: 'gpt-4o', label: 'GPT-4o (highest quality)', costPer1k: 955 },
]

const EPISODE_OPTIONS = [
  { value: 'auto', label: 'Auto (variable based on persona)' },
  { value: '5', label: '5 episodes (short)' },
  { value: '8', label: '8 episodes (medium)' },
  { value: '10', label: '10 episodes (standard)' },
]

export default function BatchGenerationPanel({ onJobComplete }) {
  const [config, setConfig] = useState({
    numJourneys: 3,
    episodesPerJourney: 'auto',
    generationModel: 'gpt-4.1-nano',
    disableTraces: false,
    combineNotes: true,
  })
  const [showConfirm, setShowConfirm] = useState(false)
  const [jobId, setJobId] = useState(null)
  const [jobStatus, setJobStatus] = useState(null)
  const [error, setError] = useState(null)
  const pollRef = useRef(null)

  const PERSONA_COST_PER_JOURNEY = 0.001 // GPT-4.1-nano persona generation

  const estimatedCost = () => {
    const model = MODEL_OPTIONS.find(m => m.value === config.generationModel)
    if (!model) return 0
    let journeyCost = (config.numJourneys / 1000) * model.costPer1k
    if (config.disableTraces) journeyCost *= 0.55
    if (config.combineNotes) journeyCost *= 0.85
    const personaCost = config.numJourneys * PERSONA_COST_PER_JOURNEY
    return (journeyCost + personaCost).toFixed(2)
  }

  const startGeneration = async () => {
    setShowConfirm(false)
    setError(null)
    try {
      const result = await batchAPI.startGeneration({
        num_journeys: config.numJourneys,
        episodes_per_journey: config.episodesPerJourney === 'auto' ? 'auto' : parseInt(config.episodesPerJourney),
        generation_model: config.generationModel,
        disable_traces: config.disableTraces,
        combine_notes: config.combineNotes,
      })
      setJobId(result.job_id)
      setJobStatus({ status: 'pending', progress: { completed: 0, failed: 0, total: config.numJourneys } })
      startPolling(result.job_id)
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start generation')
    }
  }

  const startPolling = (id) => {
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = setInterval(async () => {
      try {
        const job = await batchAPI.getJob(id)
        setJobStatus(job)
        if (['completed', 'failed', 'cancelled'].includes(job.status)) {
          clearInterval(pollRef.current)
          pollRef.current = null
          if (job.status === 'completed' && onJobComplete) onJobComplete(job)
        }
      } catch {
        // Ignore polling errors
      }
    }, 3000)
  }

  const cancelJob = async () => {
    if (!jobId) return
    try {
      await batchAPI.cancelJob(jobId)
    } catch {
      // Ignore
    }
  }

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [])

  const progress = jobStatus?.progress || {}
  const isRunning = jobStatus?.status === 'running' || jobStatus?.status === 'pending'
  const isDone = jobStatus?.status === 'completed' || jobStatus?.status === 'failed' || jobStatus?.status === 'cancelled'

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        <h3 className="card-title text-lg">Batch Generation</h3>

        {!jobId && (
          <div className="space-y-3">
            {/* Number of journeys */}
            <div className="form-control">
              <label className="label"><span className="label-text">Number of Journeys</span></label>
              <input
                type="number"
                className="input input-bordered w-full"
                value={config.numJourneys}
                min={1}
                max={50}
                onChange={e => setConfig(c => ({ ...c, numJourneys: Math.min(parseInt(e.target.value) || 1, 50) }))}
              />
            </div>

            {/* Episodes per journey */}
            <div className="form-control">
              <label className="label"><span className="label-text">Episodes per Journey</span></label>
              <select
                className="select select-bordered w-full"
                value={config.episodesPerJourney}
                onChange={e => setConfig(c => ({ ...c, episodesPerJourney: e.target.value }))}
              >
                {EPISODE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* Model */}
            <div className="form-control">
              <label className="label"><span className="label-text">Generation Model</span></label>
              <select
                className="select select-bordered w-full"
                value={config.generationModel}
                onChange={e => setConfig(c => ({ ...c, generationModel: e.target.value }))}
              >
                {MODEL_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* Optimizations */}
            <div className="flex gap-4">
              <label className="label cursor-pointer gap-2">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={config.disableTraces}
                  onChange={e => setConfig(c => ({ ...c, disableTraces: e.target.checked }))}
                />
                <span className="label-text text-sm">Disable reasoning traces (-45% tokens)</span>
              </label>
              <label className="label cursor-pointer gap-2">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={config.combineNotes}
                  onChange={e => setConfig(c => ({ ...c, combineNotes: e.target.checked }))}
                />
                <span className="label-text text-sm">Combine notes calls (-15% tokens)</span>
              </label>
            </div>

            {/* Estimated cost */}
            <div className="bg-base-200 rounded-lg p-3 text-sm">
              Estimated cost: <span className="font-semibold">${estimatedCost()}</span>
              <span className="text-base-content/50 ml-2">
                ({config.numJourneys} unique personas + journeys via {config.generationModel})
              </span>
            </div>

            {error && <div className="alert alert-error text-sm"><span>{error}</span></div>}

            <button
              className="btn btn-primary w-full"
              onClick={() => {
                if (config.numJourneys < 1 || config.numJourneys > 50) {
                  setError('Number of journeys must be between 1 and 50')
                  return
                }
                setShowConfirm(true)
              }}
            >
              Start Generation
            </button>
          </div>
        )}

        {/* Confirmation dialog */}
        {showConfirm && (
          <div className="modal modal-open">
            <div className="modal-box">
              <h3 className="font-bold text-lg">Confirm Batch Generation</h3>
              <p className="py-4">
                This will generate <strong>{config.numJourneys}</strong> journeys using <strong>{config.generationModel}</strong>.
                <br />Estimated cost: <strong>${estimatedCost()}</strong>
              </p>
              <div className="modal-action">
                <button className="btn" onClick={() => setShowConfirm(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={startGeneration}>Proceed</button>
              </div>
            </div>
          </div>
        )}

        {/* Progress */}
        {jobId && (
          <div className="space-y-3 mt-2">
            <div className="flex items-center justify-between">
              <span className={`badge ${isRunning ? 'badge-info' : isDone ? (jobStatus.status === 'completed' ? 'badge-success' : 'badge-error') : 'badge-ghost'}`}>
                {jobStatus?.status || 'unknown'}
              </span>
              {isRunning && (
                <button className="btn btn-ghost btn-xs" onClick={cancelJob}>Cancel</button>
              )}
            </div>

            <progress
              className="progress progress-primary w-full"
              value={progress.completed || 0}
              max={progress.total || 1}
            ></progress>

            <div className="text-sm text-base-content/60">
              {progress.completed || 0} / {progress.total || 0} completed
              {progress.failed > 0 && <span className="text-error ml-2">({progress.failed} failed)</span>}
            </div>

            {isDone && (
              <div className="flex gap-2">
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => { setJobId(null); setJobStatus(null) }}
                >
                  New Batch
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
